import { api } from './api';

const API_BASE = '/api/v1';

export interface LoginResponse {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
}

export interface User {
  username: string;
  email?: string;
  is_admin?: boolean;
  roles?: string[];
  permissions?: string[];
}

export interface LoginCredentials {
  username: string;
  password: string;
}

/**
 * OAuth: redirect to backend Google authorize URL (cookie-based session on callback).
 */
export function getOAuthAuthorizeUrl(): string {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return `${base}${API_BASE}/auth/oauth/google/authorize`;
}

/**
 * Start login by redirecting to Google OAuth (T055).
 */
export function loginWithRedirect(): void {
  window.location.href = getOAuthAuthorizeUrl();
}

class AuthService {
  /**
   * Redirect to Google OAuth authorize URL (T055).
   */
  loginWithRedirect(): void {
    window.location.href = getOAuthAuthorizeUrl();
  }

  /**
   * Legacy username/password login (optional; primary is OAuth).
   *
   * Suppresses the global onUnauthorized handler: a wrong-password 401 here
   * is the login form's own local error (surfaced via the caller's catch
   * block), not a dead session. Without this, the 401 would fire the
   * app-wide session-guard redirect, which hard-reloads to /login and wipes
   * the in-progress form (typed credentials, the "Invalid credentials"
   * message).
   */
  async login(username: string, password: string): Promise<LoginResponse> {
    const data = await api.post<LoginResponse>(
      '/auth/login',
      { username, password },
      { suppressUnauthorized: true }
    );
    api.setToken(data.access_token);
    return data;
  }

  /**
   * Logout: call backend to invalidate session and clear cookie (T055).
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } finally {
      api.setToken(null);
    }
  }

  /**
   * Get current user from session cookie or bearer token (GET /auth/me).
   *
   * Suppresses the global onUnauthorized handler: the session gate
   * (ProtectedRoute) owns 401 handling for revalidation — retry-after-hydration
   * or a deliberate logout — so a transient 401 here must not trigger the
   * app-wide hard redirect.
   */
  async getCurrentUser(): Promise<User> {
    return api.get<User>('/auth/me', { suppressUnauthorized: true });
  }
}

export const authService = new AuthService();
